import React from "react";
import { ArrowUpRight, ExternalLink, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface PurchaseOrderLinkSectionProps {
  purchaseOrderId: string;
  displayReceivingDiscrepancyLink?: boolean;
  initiallyOpen?: boolean;
  withCollapsible?: boolean;
}

const PurchaseOrderLinksSection: React.FC<PurchaseOrderLinkSectionProps> = ({
  purchaseOrderId,
  displayReceivingDiscrepancyLink,
  initiallyOpen = false,
  withCollapsible = true
}) => {
  const cards = [
    {
      title: "Purchase Order Overview",
      description: "Open the full purchase order to review product, shipment, and finance details.",
      to: `${ROUTES_CONFIG.purchaseOrders.path}?id=${purchaseOrderId}`,
      tone: "neutral"
    },
    displayReceivingDiscrepancyLink
      ? {
          title: "Receiving Discrepancy",
          description: "Investigate flagged discrepancies raised during warehouse check-ins.",
          to: `${ROUTES_CONFIG.receivingDiscrepancies.path}?id=${purchaseOrderId}`,
          tone: "warning"
        }
      : null
  ].filter(Boolean) as {
    title: string;
    description: string;
    to: string;
    tone: "neutral" | "warning";
  }[];

  const toneStyles: Record<"neutral" | "warning", string> = {
    neutral:
      "border-secondary-200 bg-white hover:border-primary-200 hover:shadow-md focus-visible:ring-primary-500 focus-visible:border-primary-300",
    warning:
      "border-warning-200 bg-warning-50 hover:border-warning-300 hover:shadow-md focus-visible:ring-warning-500 focus-visible:border-warning-400"
  };

  const content = (
    <div className="p-6 space-y-4">
      <p className="text-sm text-secondary-600">
        Quick navigation to related records for deeper investigation or next steps.
      </p>

      <div className="grid gap-3">
        {cards.map(({ title, description, to, tone }) => (
          <Link
            key={title}
            to={to}
            className={`group flex items-center justify-between rounded-2xl border px-4 py-4 transition-all duration-200 focus-visible:outline-none sm:px-6 ${toneStyles[tone]}`}
          >
            <div className="flex items-start gap-3">
              <span
                className={`mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold ${
                  tone === "warning"
                    ? "border-warning-200 bg-warning-100 text-warning-700"
                    : "border-secondary-200 bg-secondary-100 text-secondary-700"
                }`}
              >
                <ArrowUpRight className="h-4 w-4" />
              </span>
              <div>
                <h4 className="text-sm font-semibold text-secondary-900 sm:text-base">{title}</h4>
                <p className="mt-1 text-xs text-secondary-600 sm:text-sm">{description}</p>
              </div>
            </div>
            <ExternalLink className="h-4 w-4 shrink-0 text-secondary-400 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-secondary-600" />
          </Link>
        ))}
      </div>
    </div>
  );

  if (!withCollapsible) {
    return (
      <section className="rounded-2xl border border-secondary-200 bg-white shadow-soft">
        <div className="flex items-center gap-2 border-b border-secondary-100 px-6 py-4">
          <Link2 className="h-5 w-5 text-primary-600" />
          <h3 className="text-base font-semibold text-secondary-900">Related Links</h3>
        </div>
        {content}
      </section>
    );
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={initiallyOpen}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Link2 className="w-5 h-5 mr-2 text-primary-600" />
            Related Links
          </h3>
        }
      >
        {content}
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderLinksSection };
